// src/pages/Reviews.jsx
import React, { useState } from 'react';
import { Star, Send, CheckCircle } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { testimonials } from '../data/testimonials';
import { generateReviewEmailLink } from '../utils/helpers';

const Reviews = () => {
  const averageRating = (
    testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length
  ).toFixed(1);

  return (
    <div>
      {/* ============ PAGE HERO ============ */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="eyebrow">Customer Reviews</div>
            <h1>What Our Clients Say</h1>
            <p>Real feedback from real customers across Sheffield & surrounding areas.</p>

            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2rem',
                marginTop: '2rem',
                padding: '1.5rem 2.5rem',
                backgroundColor: 'var(--black-card)',
                border: '1px solid var(--gold-border)',
                borderRadius: 'var(--radius-2xl)',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '2.5rem',
                    fontWeight: 800,
                    color: 'var(--gold)',
                    lineHeight: 1,
                  }}
                >
                  {averageRating}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '2px',
                    justifyContent: 'center',
                    marginTop: '0.5rem',
                  }}
                >
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="var(--gold)" color="var(--gold)" />
                  ))}
                </div>
              </div>
              <div
                style={{
                  textAlign: 'left',
                  borderLeft: '1px solid var(--black-border)',
                  paddingLeft: '2rem',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: 'var(--white)',
                  }}
                >
                  {testimonials.length}+
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>
                  Happy Customers
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CUSTOMER TESTIMONIALS ============ */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="eyebrow">Testimonials</div>
            <h2>Recent Feedback</h2>
            <p>What our customers have said after their valeting service.</p>
          </div>

          <div className="grid grid-3">
            {testimonials.map((review) => (
              <Card key={review.id} hover className="review-card">
                <div className="review-rating">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="var(--gold)" color="var(--gold)" />
                  ))}
                </div>

                <p className="review-text">"{review.comment}"</p>

                <div className="review-service-badge">{review.service}</div>

                <div className="review-author">
                  <div className="review-author-name">{review.name}</div>
                  <div className="review-author-location">{review.location}</div>
                  <div className="review-author-date">
                    {new Date(review.date).toLocaleDateString('en-GB', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WRITE A REVIEW ============ */}
      <WriteReviewSection />
    </div>
  );
};

/* =========================================================
   WRITE A REVIEW SECTION
   Submits via email (mailto) — no backend required.
   ========================================================= */
const WriteReviewSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    service: '',
    rating: 5,
    comment: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter your name';
    if (!formData.service) newErrors.service = 'Please select the service you received';
    if (!formData.comment.trim()) newErrors.comment = 'Please write a short review';
    if (formData.rating < 1 || formData.rating > 5)
      newErrors.rating = 'Please select a rating';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    window.location.href = generateReviewEmailLink(formData);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <section className="section section-black">
        <div className="container" style={{ maxWidth: 600, textAlign: 'center' }}>
          <CheckCircle size={64} color="var(--gold)" style={{ margin: '0 auto 1.5rem' }} />
          <h2 style={{ color: 'var(--white)' }}>Thank You!</h2>
          <p style={{ color: 'var(--gray)', marginBottom: '1.5rem' }}>
            Your review has been prepared in your email client. Please send the
            email so we can add it to our site.
          </p>
          <button
            className="btn btn-outline"
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: '', location: '', service: '', rating: 5, comment: '' });
            }}
          >
            Write Another Review
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="section section-black">
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="section-header">
          <div className="eyebrow">Write a Review</div>
          <h2>Share Your Experience</h2>
          <p>Had a valeting service with us? We'd love to hear your feedback.</p>
        </div>

        <Card style={{ padding: '2rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {/* Rating */}
            <div className="form-group">
              <label className="form-label">
                Your Rating <span className="required">*</span>
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleChange('rating', star)}
                    aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      transition: 'transform 0.2s ease',
                      transform: formData.rating >= star ? 'scale(1.1)' : 'scale(1)',
                    }}
                  >
                    <Star
                      size={32}
                      fill={formData.rating >= star ? 'var(--gold)' : 'transparent'}
                      color={formData.rating >= star ? 'var(--gold)' : 'var(--gray-mid)'}
                    />
                  </button>
                ))}
                <span
                  style={{
                    marginLeft: '0.75rem',
                    color: 'var(--gray)',
                    fontSize: '0.875rem',
                  }}
                >
                  {formData.rating} / 5
                </span>
              </div>
              {errors.rating && <div className="form-error">{errors.rating}</div>}
            </div>

            {/* Name */}
            <div className="form-group">
              <label className="form-label">
                Your Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="e.g. Sarah J."
              />
              {errors.name && <div className="form-error">{errors.name}</div>}
            </div>

            {/* Location */}
            <div className="form-group">
              <label className="form-label">Your Location (Optional)</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="form-input"
                placeholder="e.g. Sheffield"
              />
            </div>

            {/* Service */}
            <div className="form-group">
              <label className="form-label">
                Service Received <span className="required">*</span>
              </label>
              <select
                value={formData.service}
                onChange={(e) => handleChange('service', e.target.value)}
                className={`form-select ${errors.service ? 'error' : ''}`}
              >
                <option value="">Select a service...</option>
                <option value="Bronze Package">Bronze Package</option>
                <option value="Silver Package">Silver Package</option>
                <option value="Gold Package">Gold Package</option>
                <option value="Platinum Package">Platinum Package</option>
                <option value="Additional Service">Additional Service</option>
              </select>
              {errors.service && <div className="form-error">{errors.service}</div>}
            </div>

            {/* Comment */}
            <div className="form-group">
              <label className="form-label">
                Your Review <span className="required">*</span>
              </label>
              <textarea
                value={formData.comment}
                onChange={(e) => handleChange('comment', e.target.value)}
                className={`form-textarea ${errors.comment ? 'error' : ''}`}
                placeholder="Tell us about your experience..."
                rows={5}
              />
              {errors.comment && <div className="form-error">{errors.comment}</div>}
            </div>

            <Button
              variant="primary"
              size="lg"
              className="w-full"
              icon={Send}
              onClick={handleSubmit}
            >
              Submit Review
            </Button>

            <p
              style={{
                fontSize: '0.8125rem',
                color: 'var(--gray-dark)',
                textAlign: 'center',
                marginTop: '0.75rem',
              }}
            >
              Your review will open in your email client. We'll publish it within 24 hours.
            </p>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default Reviews;